<template>
  <div class="container">
    <h2 class="title text-size-4 tablet:text-size-3 my-size-4 tablet:my-size-2">
      {{ paragraphTitle }}
    </h2>
    <ParagraphFAQQuestion
      v-for="question in questions"
      :id="question.id"
      :key="question.id"
      :name="question.name"
      :answer="question.answer"
      :link="question.link"
    />
  </div>
</template>

<script setup lang="ts">
import { notNullish } from '@vueuse/core'
import type {
  ParagraphFaqFragment,
  StorageFaqFragment,
  FaqsByCategoryQueryVariables,
} from '#graphql-operations'

const props = defineProps<{
  id?: ParagraphFaqFragment['id']
  title?: ParagraphFaqFragment['title']
  category?: ParagraphFaqFragment['category']
}>()

defineBlokkli({
  bundle: 'faq',
})
const { $texts } = useEasyTexts()
const language = useCurrentLanguage()

const variables = computed<FaqsByCategoryQueryVariables>(() => {
  return {
    categoryId: props.category?.[0]?.id || '',
  }
})

const { data } = await useAsyncGraphqlQuery('faqsByCategory', variables, {
  transform: (data) => {
    return (data.data.entityQuery.items || []).filter(
      notNullish,
    ) as StorageFaqFragment[]
  },
  deep: false,
  graphqlCaching: {
    client: true,
  },
  watch: [language],
})

const questions = computed(() => data.value)
const paragraphTitle = computed(() => {
  if (props.title) {
    return `${props.title} ${props.category?.[0]?.name}`
  }
  return `${$texts('faq', 'Frequently asked questions')} ${props.category?.[0]?.name}`
})

const schemaOrg = computed(() => {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        mainEntity: questions.value?.map((question) => {
          return {
            '@type': 'Question',
            name: question.name,
            acceptedAnswer: {
              '@type': 'Answer',
              text: question.answer,
            },
          }
        }),
      },
    ],
  }
})

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schemaOrg.value),
    },
  ],
})
</script>
